import * as XLSX from 'xlsx';
import type { MenuItem } from '@/lib/data';

/* Excel/CSV menu import — full field set. Generates a downloadable .xlsx
   template and parses uploaded .xlsx/.csv files into typed rows. Category is
   kept as a NAME here; callers resolve it to a category id (creating missing
   categories) before saving. */

export type FoodType = 'veg' | 'non-veg' | 'egg';

export interface MenuSheetRow {
  name: string;
  categoryName: string | null;
  description: string | null;
  foodType: FoodType;
  price: number;
  gstRate: number;
  hsnCode: string;
  prepTime: number;
  available: boolean;
  recommended: boolean;
  dineIn: boolean;
  takeaway: boolean;
  online: boolean;
  variants: { name: string; price: number }[];
  addons: { name: string; price: number }[];
  image: string | null;
  /** Column keys that actually had a value in the sheet. Blank cells fall back
      to defaults for NEW items, and are left untouched on updates — so an empty
      cell never counts as a change. */
  provided: string[];
}

export const MENU_COLUMNS = [
  'Name', 'Category', 'Description', 'Food Type', 'Price', 'GST %', 'HSN',
  'Prep Time (min)', 'Available', 'Bestseller', 'Dine-in', 'Takeaway', 'Online',
  'Variants', 'Add-ons', 'Image URL',
] as const;

const SAMPLE_ROWS: (string | number)[][] = [
  ['Paneer Tikka', 'Starters', 'Char-grilled cottage cheese', 'veg', 220, 5, '9963', 15,
   'Yes', 'Yes', 'Yes', 'Yes', 'Yes', 'Half:160 | Full:220', 'Extra Cheese:30', ''],
  ['Butter Chicken', 'Main Course', 'Creamy tomato gravy', 'non-veg', 320, 5, '9963', 20,
   'Yes', 'No', 'Yes', 'Yes', 'Yes', '', 'Extra Gravy:40 | Boneless:60', ''],
];

function yesNo(b: boolean): string { return b ? 'Yes' : 'No'; }
function encodePairs(pairs: { name: string; price: number }[]): string {
  return (pairs ?? []).map((p) => `${p.name}:${p.price}`).join(' | ');
}

function parseBool(v: unknown, dflt: boolean): boolean {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === '') return dflt;
  return ['yes', 'y', 'true', '1', '✓'].includes(s);
}
function parseFoodType(v: unknown): FoodType {
  const s = String(v ?? '').trim().toLowerCase().replace(/[\s_]/g, '-');
  if (s.startsWith('non')) return 'non-veg';
  if (s.startsWith('egg')) return 'egg';
  return 'veg';
}
function parsePairs(v: unknown): { name: string; price: number }[] {
  const s = String(v ?? '');
  if (!s.trim()) return [];
  return s.split('|').map((chunk) => {
    const idx = chunk.lastIndexOf(':');
    const name = (idx >= 0 ? chunk.slice(0, idx) : chunk).trim();
    const price = idx >= 0 ? Number(chunk.slice(idx + 1).trim()) || 0 : 0;
    return { name, price };
  }).filter((p) => p.name);
}

/** Download a filled .xlsx template (headers + sample rows) with all fields. */
export function downloadMenuTemplate(filename = 'menu_import_template.xlsx'): void {
  const ws = XLSX.utils.aoa_to_sheet([[...MENU_COLUMNS], ...SAMPLE_ROWS]);
  // Reasonable column widths.
  ws['!cols'] = MENU_COLUMNS.map((c) => ({ wch: Math.max(12, c.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Menu');
  XLSX.writeFile(wb, filename);
}

/** Build the sheet rows (header + one row per item) for export. `catName`
    resolves a stored category id → its display name. */
export function menuItemsToRows(
  items: MenuItem[],
  catName: (id: string | null) => string,
): (string | number)[][] {
  return [
    [...MENU_COLUMNS],
    ...items.map((it) => [
      it.name,
      catName(it.category),
      it.description ?? '',
      it.foodType,
      it.price,
      it.gstRate,
      it.hsnCode ?? '',
      it.prepTime ?? '',
      yesNo(it.available),
      yesNo(it.recommended),
      yesNo(it.dineIn ?? true),
      yesNo(it.takeaway ?? true),
      yesNo(it.online ?? true),
      encodePairs(it.variants ?? []),
      encodePairs(it.addons ?? []),
      it.image ?? '',
    ]),
  ];
}

/** Download the given items as a filled .xlsx (same columns as the template). */
export function downloadMenuXlsx(
  filename: string,
  items: MenuItem[],
  catName: (id: string | null) => string,
): void {
  const ws = XLSX.utils.aoa_to_sheet(menuItemsToRows(items, catName));
  ws['!cols'] = MENU_COLUMNS.map((c) => ({ wch: Math.max(12, c.length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Menu');
  XLSX.writeFile(wb, filename);
}

/** Parse an uploaded .xlsx / .xls / .csv file into typed rows (order-independent
    columns, matched by header). Rows without a name are dropped. */
export async function parseMenuSheet(file: File): Promise<MenuSheetRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];
  const grid = XLSX.utils.sheet_to_json<(string | number)[]>(ws, { header: 1, blankrows: false, defval: '' });
  if (grid.length < 2) return [];

  const header = grid[0].map((h) => String(h).trim().toLowerCase());
  const col = (name: string) => header.indexOf(name.toLowerCase());
  const idx = {
    name: col('Name'), category: col('Category'), description: col('Description'),
    foodType: col('Food Type'), price: col('Price'), gst: col('GST %'), hsn: col('HSN'),
    prep: col('Prep Time (min)'), available: col('Available'), best: col('Bestseller'),
    dineIn: col('Dine-in'), takeaway: col('Takeaway'), online: col('Online'),
    variants: col('Variants'), addons: col('Add-ons'), image: col('Image URL'),
  };
  if (idx.name < 0) throw new Error('Missing required "Name" column. Use the downloaded template.');

  const get = (r: (string | number)[], i: number) => (i >= 0 ? String(r[i] ?? '').trim() : '');

  const out: MenuSheetRow[] = [];
  for (const r of grid.slice(1)) {
    const name = get(r, idx.name);
    if (!name) continue;
    const variants = parsePairs(get(r, idx.variants));
    const addons = parsePairs(get(r, idx.addons));
    let price = Number(get(r, idx.price)) || 0;
    if (variants.length > 0) {
      const vp = variants.map((v) => v.price).filter((p) => p > 0);
      if (vp.length > 0) price = Math.min(...vp);
    }
    const gstRaw = get(r, idx.gst);

    // Track which cells were actually filled in, so blanks aren't treated as edits.
    const provided: string[] = [];
    const mark = (key: string, i: number) => { if (get(r, i) !== '') provided.push(key); };
    mark('category', idx.category); mark('description', idx.description);
    mark('foodType', idx.foodType); mark('price', idx.price);
    mark('gstRate', idx.gst); mark('hsnCode', idx.hsn);
    mark('prepTime', idx.prep); mark('available', idx.available);
    mark('recommended', idx.best); mark('dineIn', idx.dineIn);
    mark('takeaway', idx.takeaway); mark('online', idx.online);
    mark('variants', idx.variants); mark('addons', idx.addons);
    mark('image', idx.image);

    out.push({
      name,
      categoryName: get(r, idx.category) || null,
      description: get(r, idx.description) || null,
      foodType: parseFoodType(get(r, idx.foodType)),
      price,
      gstRate: gstRaw === '' ? 5 : Number(gstRaw) || 0,
      hsnCode: get(r, idx.hsn) || '9963',
      prepTime: Number(get(r, idx.prep)) || 10,
      available: parseBool(get(r, idx.available), true),
      recommended: parseBool(get(r, idx.best), false),
      dineIn: parseBool(get(r, idx.dineIn), true),
      takeaway: parseBool(get(r, idx.takeaway), true),
      online: parseBool(get(r, idx.online), true),
      variants,
      addons,
      image: get(r, idx.image) || null,
      provided,
    });
  }
  return out;
}

/* ───────────── Import diff / plan ─────────────
   Compare parsed rows against what's already saved so we only write the rows
   that are genuinely new or changed. Unchanged rows are skipped entirely. */

export type MenuImportStatus = 'new' | 'update' | 'unchanged' | 'error';

export interface MenuImportPlanRow {
  row: MenuSheetRow;
  status: MenuImportStatus;
  message?: string;
  /** Human-readable list of fields that differ (for 'update' rows). */
  changed: string[];
  existing?: MenuItem;
}

const norm = (v: string | null | undefined) => (v ?? '').toString().trim();
const samePairs = (
  a: { name: string; price: number }[],
  b: { name: string; price: number }[],
) => {
  const key = (p: { name: string; price: number }[]) =>
    p.map((x) => `${norm(x.name).toLowerCase()}:${Number(x.price) || 0}`).sort().join('|');
  return key(a ?? []) === key(b ?? []);
};

/** Build the import plan: new / update (with changed fields) / unchanged / error. */
export function planMenuImport(
  rows: MenuSheetRow[],
  existing: MenuItem[],
  catName: (id: string | null) => string,
): MenuImportPlanRow[] {
  const byName = new Map(existing.map((i) => [i.name.trim().toLowerCase(), i]));
  return rows.map((row): MenuImportPlanRow => {
    if (!row.name) return { row, status: 'error', message: 'Name is required', changed: [] };
    if (row.price <= 0) {
      return { row, status: 'error', message: 'Needs a price (or a priced variant)', changed: [] };
    }
    const ex = byName.get(row.name.trim().toLowerCase());
    if (!ex) return { row, status: 'new', changed: [] };

    const has = (k: string) => row.provided.includes(k);
    const changed: string[] = [];
    const cmp = (label: string, key: string, a: unknown, b: unknown) => {
      if (has(key) && a !== b) changed.push(label);
    };

    if (has('category') && norm(row.categoryName).toLowerCase() !== norm(catName(ex.category)).toLowerCase()) {
      changed.push('Category');
    }
    cmp('Description', 'description', norm(row.description), norm(ex.description));
    cmp('Food Type', 'foodType', row.foodType, ex.foodType);
    cmp('Price', 'price', row.price, Number(ex.price));
    cmp('GST %', 'gstRate', row.gstRate, Number(ex.gstRate));
    cmp('HSN', 'hsnCode', norm(row.hsnCode), norm(ex.hsnCode));
    cmp('Prep Time', 'prepTime', row.prepTime, Number(ex.prepTime));
    cmp('Available', 'available', row.available, !!ex.available);
    cmp('Bestseller', 'recommended', row.recommended, !!ex.recommended);
    cmp('Dine-in', 'dineIn', row.dineIn, ex.dineIn ?? true);
    cmp('Takeaway', 'takeaway', row.takeaway, ex.takeaway ?? true);
    cmp('Online', 'online', row.online, ex.online ?? true);
    cmp('Image', 'image', norm(row.image), norm(ex.image));
    if (has('variants') && !samePairs(row.variants, ex.variants ?? [])) changed.push('Variants');
    if (has('addons') && !samePairs(row.addons, ex.addons ?? [])) changed.push('Add-ons');

    return { row, status: changed.length ? 'update' : 'unchanged', changed, existing: ex };
  });
}

/** Build the DB payload for a planned row. Updates only carry the columns that
    were actually filled in the sheet; new rows carry everything (with defaults). */
export function rowToPayload(
  plan: MenuImportPlanRow,
  categoryId: string | null,
): Partial<MenuItem> & { name: string } {
  const { row, existing } = plan;
  const isNew = !existing;
  const has = (k: string) => isNew || row.provided.includes(k);
  const p: Partial<MenuItem> & { name: string } = { name: row.name, price: row.price };
  if (existing) p.id = existing.id;
  if (has('category')) p.category = categoryId;
  if (has('description')) p.description = row.description;
  if (has('foodType')) p.foodType = row.foodType;
  if (has('gstRate')) p.gstRate = row.gstRate;
  if (has('hsnCode')) p.hsnCode = row.hsnCode;
  if (has('prepTime')) p.prepTime = row.prepTime;
  if (has('available')) p.available = row.available;
  if (has('recommended')) p.recommended = row.recommended;
  if (has('dineIn')) p.dineIn = row.dineIn;
  if (has('takeaway')) p.takeaway = row.takeaway;
  if (has('online')) p.online = row.online;
  if (has('variants')) p.variants = row.variants;
  if (has('addons')) p.addons = row.addons;
  if (has('image')) p.image = row.image;
  return p;
}
